<template>
  <sign-up-wizard v-if="isEmbedded" :show.sync="wizardVisible" @update:show="onWizardClose" />
</template>

<script>
import SignUpWizard from "@/views/login/SignUpWizard.vue";

// Parent origins we'll post the close message to. Pinning the target
// origin prevents a rogue embedder from listening to the close signal.
// Never use '*'. The referrer's origin is also appended at runtime so
// any legitimate embedder (including local dev) works automatically.
const KNOWN_PARENT_ORIGINS = [
  'https://kiey.com',
  'https://www.kiey.com',
  'https://staging.kiey.com',
];

export default {
  name: "SignupLanding",
  components: {
    "sign-up-wizard": SignUpWizard,
  },
  data() {
    return {
      wizardVisible: true,
      isEmbedded: false,
    };
  },
  created() {
    try {
      this.isEmbedded = window.self !== window.top;
    } catch (e) {
      this.isEmbedded = true;
    }
    // When a user lands on /signup directly (not embedded in an iframe),
    // redirect them to /login?signup=true so the login page renders
    // behind the wizard. Closing the wizard then leaves them on login
    // instead of a blank page. The iframe embed path stays on /signup
    // because kiey.com is the visual backdrop in that case.
    if (!this.isEmbedded) {
      // Preserve any preset-role hint (?signup=vendor / ?signup=client /
      // ?signup=agent) so the wizard can skip the role picker. Without
      // this, the redirect would clobber the query and every preset
      // would fall back to the role picker.
      const incoming = this.$route.query.signup;
      const signupQuery = incoming && typeof incoming === 'string' ? incoming : 'true';
      this.$router.replace({ path: '/login', query: { signup: signupQuery } });
    }
  },
  mounted() {
    if (this.isEmbedded) {
      document.body.classList.add('kiey-signup-route');
      document.body.classList.add('kiey-signup-embedded');
    }
  },
  beforeDestroy() {
    document.body.classList.remove('kiey-signup-route');
    document.body.classList.remove('kiey-signup-embedded');
  },
  methods: {
    onWizardClose(val) {
      if (val !== false) return;
      // Only reached in the iframe-embedded path — tell the host page
      // to tear down the iframe overlay. Browser only delivers to a
      // matching parent origin, so extra targets are no-ops.
      const targets = KNOWN_PARENT_ORIGINS.slice();
      try {
        if (document.referrer) {
          const refOrigin = new URL(document.referrer).origin;
          if (refOrigin && !targets.includes(refOrigin)) targets.unshift(refOrigin);
        }
      } catch (e) { /* noop */ }
      for (const origin of targets) {
        try { window.parent.postMessage({ type: 'kiey-signup-close' }, origin); } catch (e) { /* noop */ }
      }
    },
  },
};
</script>

<style>
/* Prevent scroll behind the modal overlay on the /signup route. */
body.kiey-signup-route { overflow: hidden; }

/* When embedded in an iframe on kiey.com, make the app chrome transparent
 * so the host page's backdrop shows through. The SignUpWizard's own
 * overlay paints the blur+dim behind the modal card. */
body.kiey-signup-embedded,
body.kiey-signup-embedded #app-root {
  background: transparent !important;
}
</style>
